import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, TrendingUp, Clock, Mountain, CheckCircle, Filter, Map, Crown, AlertCircle, Route as RouteIcon, X, Bike, Footprints, Car, Users, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import RangeSlider from '@/components/RangeSlider';
import FavoriteButton from '@/components/FavoriteButton';
import './RoutesPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RoutesPage() {
  const { t } = useTranslation();
  const { token, isPremium } = useAuth();
  const [searchParams] = useSearchParams();
  const [routes, setRoutes] = useState([]);
  const [allRoutes, setAllRoutes] = useState([]); // Store all routes for client-side filtering
  const [loading, setLoading] = useState(true);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [childFriendlyOnly, setChildFriendlyOnly] = useState(false);
  const [filters, setFilters] = useState({
    activity_type: searchParams.get('activity_type') || '',
    region: searchParams.get('region') || '',
    difficulty: '',
    bike_type: '',
    route_type: '',
    distance: [0, 200],
    elevation: [0, 3000]
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRoutes();
  }, []);

  // Re-fetch when activity_type changes (important for free users!)
  useEffect(() => {
    if (filters.activity_type !== '') {
      fetchRoutes();
    }
  }, [filters.activity_type]);

  useEffect(() => {
    applyFilters();
  }, [filters, allRoutes, childFriendlyOnly]);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Build query params - include activity_type for backend filtering
      const params = {};
      if (filters.activity_type) {
        params.activity_type = filters.activity_type;
      }
      
      const response = await axios.get(`${API}/routes`, { headers, params });
      setAllRoutes(response.data);
      
      // Show warning if free user and seeing limited results
      if (!isPremium && response.data.length === 10) {
        setShowLimitWarning(true);
      } else {
        setShowLimitWarning(false);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allRoutes];

    // DON'T filter activity_type here - backend does it now!
    // This ensures free users get fresh data from backend

    // Apply child-friendly filter
    if (childFriendlyOnly) {
      filtered = filtered.filter(r => r.is_child_friendly === true);
    }

    // Apply dropdown filters
    if (filters.region) {
      filtered = filtered.filter(r => r.region === filters.region);
    }
    if (filters.difficulty) {
      filtered = filtered.filter(r => r.difficulty === filters.difficulty);
    }
    if (filters.bike_type) {
      filtered = filtered.filter(r => r.bike_type === filters.bike_type || r.bike_type === 'all');
    }
    if (filters.route_type) {
      filtered = filtered.filter(r => r.route_type === filters.route_type);
    }

    // Apply range filters
    filtered = filtered.filter(r => 
      r.distance_km >= filters.distance[0] && 
      r.distance_km <= filters.distance[1]
    );
    filtered = filtered.filter(r => 
      r.elevation_gain_m >= filters.elevation[0] && 
      r.elevation_gain_m <= filters.elevation[1]
    );

    setRoutes(filtered);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      activity_type: '',
      region: '',
      difficulty: '',
      bike_type: '',
      route_type: '',
      distance: [0, 200],
      elevation: [0, 3000]
    });
  };

  const isRouteNew = (route) => {
    if (!route.created_at && !route.updated_at) return false;
    
    const dateToCheck = route.updated_at || route.created_at;
    const routeDate = new Date(dateToCheck);
    const now = new Date();
    const daysDiff = (now - routeDate) / (1000 * 60 * 60 * 24);
    
    return daysDiff <= 14; // New if updated/created in last 14 days
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.activity_type) count++;
    if (filters.region) count++;
    if (filters.difficulty) count++;
    if (filters.bike_type) count++;
    if (filters.route_type) count++;
    if (filters.distance[0] !== 0 || filters.distance[1] !== 200) count++;
    if (filters.elevation[0] !== 0 || filters.elevation[1] !== 3000) count++;
    return count;
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      easy: '#4ade80',
      moderate: '#fbbf24',
      hard: '#fb923c',
      expert: '#ef4444'
    };
    return colors[difficulty] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="loading-container" data-testid="routes-loading">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="routes-page" data-testid="routes-page">
      <header className="page-header">
        <div>
          <h1 className="page-title" data-testid="routes-title">{t('routes.title')}</h1>
          <p className="page-subtitle">{t('routes.subtitle')}</p>
        </div>
        <div className="header-actions">
          {isPremium && (
            <Link to="/create-route" className="create-route-btn">
              <RouteIcon size={20} />
              {t('routes.create_route')}
            </Link>
          )}
          <button 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle-btn"
          >
            <Filter size={20} />
            {showFilters ? t('routes.filter_hide') : t('routes.filter_show')}
            {getActiveFilterCount() > 0 && (
              <span className="filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>
        </div>
      </header>

      {showLimitWarning && (
        <div className="premium-warning">
          <AlertCircle size={24} />
          <div className="premium-warning-content">
            <h3>
              <Crown size={20} />
              {t('routes.limited_warning_title')}
            </h3>
            <p>{t('routes.limited_warning_text')}</p>
            <Link to="/profile" className="upgrade-button">
              {t('routes.upgrade_now')}
            </Link>
          </div>
        </div>
      )}

      {showFilters && (
        <div className="filters-panel" data-testid="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>{t('filters.activity_type')}</label>
              <select 
                value={filters.activity_type} 
                onChange={(e) => handleFilterChange('activity_type', e.target.value)}
                data-testid="filter-activity"
              >
                <option value="">{t('filters.all')}</option>
                <option value="cycling">🚴 {t('activities.cycling')}</option>
                <option value="hiking">🥾 {t('activities.hiking')}</option>
                <option value="roadtrip">🚗 {t('activities.roadtrip')}</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>{t('filters.region')}</label>
              <select 
                value={filters.region} 
                onChange={(e) => handleFilterChange('region', e.target.value)}
                data-testid="filter-region"
              >
                <option value="">{t('filters.all')}</option>
                <option value="Highlands">{t('regions.Highlands')}</option>
                <option value="Islands">{t('regions.Islands')}</option>
                <option value="Borders">{t('regions.Borders')}</option>
                <option value="Cities">{t('regions.Cities')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('filters.difficulty')}</label>
              <select 
                value={filters.difficulty} 
                onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                data-testid="filter-difficulty"
              >
                <option value="">{t('filters.all')}</option>
                <option value="easy">{t('difficulties.easy')}</option>
                <option value="moderate">{t('difficulties.moderate')}</option>
                <option value="hard">{t('difficulties.hard')}</option>
                <option value="expert">{t('difficulties.expert')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('filters.bike_type')}</label>
              <select 
                value={filters.bike_type} 
                onChange={(e) => handleFilterChange('bike_type', e.target.value)}
                data-testid="filter-bike-type"
              >
                <option value="">{t('bike_types.all')}</option>
                <option value="normal">{t('bike_types.normal')}</option>
                <option value="ebike">{t('bike_types.ebike')}</option>
                <option value="mountain">{t('bike_types.mountain')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('filters.route_type')}</label>
              <select 
                value={filters.route_type} 
                onChange={(e) => handleFilterChange('route_type', e.target.value)}
                data-testid="filter-route-type"
              >
                <option value="">{t('filters.all')}</option>
                <option value="road">{t('route_types.road')}</option>
                <option value="gravel">{t('route_types.gravel')}</option>
                <option value="mountain">{t('route_types.mountain')}</option>
                <option value="mixed">{t('route_types.mixed')}</option>
              </select>
            </div>

            <div className="filter-group filter-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={childFriendlyOnly}
                  onChange={(e) => setChildFriendlyOnly(e.target.checked)}
                  data-testid="filter-child-friendly"
                />
                <Users size={18} style={{ marginLeft: '8px', marginRight: '4px' }} />
                {t('routes.child_friendly_only')}
              </label>
            </div>
          </div>

          <div className="filters-range">
            <RangeSlider
              label={t('routes.distance_label')}
              min={0}
              max={200}
              value={filters.distance}
              onChange={(value) => handleFilterChange('distance', value)}
              unit="km"
              step={5}
            />
            
            <RangeSlider
              label={t('routes.elevation_label')}
              min={0}
              max={3000}
              value={filters.elevation}
              onChange={(value) => handleFilterChange('elevation', value)}
              unit="m"
              step={100}
            />
          </div>
          
          {getActiveFilterCount() > 0 && (
            <button 
              className="clear-filters-btn"
              onClick={clearFilters}
              data-testid="clear-filters-btn"
            >
              <X size={18} />
              {t('routes.clear_filters')} ({getActiveFilterCount()})
            </button>
          )}
        </div>
      )}

      <div className="routes-count" data-testid="routes-count">
        {routes.length} {t('routes.routes_found')}
      </div>

      {routes.length === 0 ? (
        <div className="no-routes" data-testid="no-routes">
          <Mountain size={64} color="var(--text-secondary)" />
          <p>{t('no_data')}</p>
        </div>
      ) : (
        <div className="routes-grid">
          {routes.map((route) => (
            <div key={route.id} className="route-card" data-testid={`route-card-${route.id}`}>
              <div className="route-card-favorite">
                <FavoriteButton itemType="route" itemId={route.id} size={20} />
              </div>
              <Link to={`/routes/${route.id}`} className="route-card-link">
                <div className="route-header">
                  <div className="route-tags">
                    {route.activity_type && (
                      <span className={`route-activity activity-${route.activity_type}`}>
                        {route.activity_type === 'cycling' && <Bike size={14} />}
                        {route.activity_type === 'hiking' && <Footprints size={14} />}
                        {route.activity_type === 'roadtrip' && <Car size={14} />}
                        {t(`activities.${route.activity_type}`)}
                      </span>
                    )}
                    <span className="route-region">{t(`regions.${route.region}`)}</span>
                    {route.is_verified && (
                      <span className="route-verified" data-testid="verified-badge">
                        <CheckCircle size={14} />
                        {t('route.verified')}
                      </span>
                    )}
                    {route.rating && route.rating >= 4.5 && (
                      <span className="route-popular" title="Beliebte Route">
                        <Users size={14} />
                        Beliebt
                      </span>
                    )}
                    {isRouteNew(route) && (
                      <span className="route-new" title="Kürzlich aktualisiert">
                        <Sparkles size={14} />
                        Neu
                      </span>
                    )}
                    {route.is_child_friendly && (
                      <span className="route-child-friendly" data-testid="child-friendly-badge">
                        <Users size={14} />
                        {t('routes.child_friendly')}
                      </span>
                    )}
                  </div>
                  <div 
                    className="route-difficulty"
                    style={{ background: getDifficultyColor(route.difficulty) }}
                    data-testid="difficulty-badge"
                  >
                    {t(`difficulties.${route.difficulty}`)}
                  </div>
                </div>

                <h3 className="route-name" data-testid="route-name">{route.name}</h3>
                <p className="route-description">{route.description}</p>

                <div className="route-stats">
                  <div className="route-stat">
                    <MapPin size={18} />
                    <span>{route.distance_km} km</span>
                  </div>
                  <div className="route-stat">
                    <TrendingUp size={18} />
                    <span>{route.elevation_gain_m} m</span>
                  </div>
                  <div className="route-stat">
                    <Clock size={18} />
                    <span>{route.duration_hours.toFixed(2)} {t('route.hours')}</span>
                  </div>
                </div>

                <div className="route-meta">
                  <span className="meta-badge">{t(`route_types.${route.route_type}`)}</span>
                  {route.tags.map((tag, idx) => (
                    <span key={idx} className="meta-tag">#{tag}</span>
                  ))}
                </div>
              </Link>

              <Link 
                to={`/map?routeId=${route.id}`}
                className="route-map-link"
              >
                <Map size={18} />
                {t('routes.show_on_map')}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
